import { FC, ReactNode, useId } from "react";
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  PromptInputActionAddAttachments,
} from "@altimateai/lego";
import { useChatContext } from "../../ChatProvider";
import { Stack } from "@altimateai/lego";
import { updateContext } from "../../store/chatSlice";
import { ContextOption, LoadingState } from "../../types";
import {
  IconPlus,
  IconSquare,
  IconSquareCheckFilled,
} from "@tabler/icons-react";

interface ContextPanelProps {
  fileUploadProps?: {
    allowedFiles: string;
    contextFieldKey: string;
  };
  contextOptions?: ContextOption[];
  children?: ReactNode[];
}

export const ContextPanel: FC<ContextPanelProps> = ({
  children,
  fileUploadProps,
  contextOptions,
}) => {
  const panelId = useId();
  const listboxId = useId();
  // const [selectedOptions, setSelectedOptions] = useState<{ [x: string]: string[] }>({});

  const { dispatch, currentSessionId, disableContext, loadingState, sessions } =
    useChatContext();
  const currentContext = currentSessionId
    ? sessions[currentSessionId]?.context || {}
    : {};

  const handleOptionSelect = (
    option: { id: string; label: string },
    key: string,
  ) => {
    const getSelectedOptions = () => {
      const values = currentContext[key] as string[] | undefined;
      if (values?.includes(option.id)) {
        return {
          ...currentContext,
          [key]: values?.filter((id) => id !== option.id),
        };
      }
      return { ...currentContext, [key]: [...(values || []), option.id] };
    };

    if (currentSessionId) {
      dispatch(
        updateContext({
          sessionId: currentSessionId,
          context: getSelectedOptions(),
        }),
      );
    }
  };

  if (disableContext) {
    return <div />;
  }

  return (
    <Stack
      id={panelId}
      role="region"
      aria-label="Context selection"
      direction="row"
      align="center"
      className="al-gap-2"
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div className="al-relative">
            <Button
              type="button"
              variant="outline"
              title="Add context or files"
              aria-controls={listboxId}
              aria-haspopup="listbox"
              aria-label="Add Context"
              disabled={loadingState === LoadingState.LOADING}
            >
              <IconPlus className="al-h-5 al-w-5" aria-hidden="true" />
            </Button>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="al-p-0" align="start">
          {fileUploadProps && <PromptInputActionAddAttachments />}
          {contextOptions?.map((contextOption) => {
            if (contextOption.type === "submenu") {
              return (
                <DropdownMenuSub key={contextOption.key}>
                  <DropdownMenuSubTrigger>
                    {contextOption.icon}
                    {contextOption.label}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="!al-p-0">
                    {/*
                      We can't use a standard <Combobox> here because nesting it inside a <DropdownMenu>
                      causes issues with event handling and focus management, leading to unexpected
                      behavior and crashes.

                      Instead, we use a <Command> component inside a <DropdownMenuItem>. The item's
                      `onSelect` event is prevented from closing the dropdown, allowing the user to
                      interact with the searchable, multi-select list provided by the <Command> component.
                    */}
                    <DropdownMenuItem
                      onSelect={(e) => e.preventDefault()}
                      className="!al-p-0 al-cursor-default focus:al-bg-transparent"
                    >
                      <Command className={"!al-p-0"}>
                        <CommandInput placeholder="Search..." />
                        <CommandList className={"!al-p-0"}>
                          <CommandEmpty>No results found.</CommandEmpty>
                          <CommandGroup>
                            {contextOption.options?.map((option) => (
                              <CommandItem
                                key={option.id}
                                value={option.label}
                                onSelect={() =>
                                  handleOptionSelect(option, contextOption.key)
                                }
                                className="al-flex al-items-center"
                              >
                                {(
                                  currentContext[contextOption.key] as string[]
                                )?.includes(option.id) ? (
                                  <IconSquareCheckFilled
                                    className="al-h-4 al-w-4 al-text-primary"
                                    role="checkbox"
                                    aria-checked="true"
                                  />
                                ) : (
                                  <IconSquare
                                    role="checkbox"
                                    aria-checked="false"
                                    className="al-h-4 al-w-4 al-text-muted-foreground"
                                  />
                                )}
                                {option.label}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              );
            }
            if (contextOption.type === "trigger") {
              return (
                <DropdownMenuItem
                  key={contextOption.key}
                  onSelect={contextOption.handler}
                >
                  {contextOption.icon}
                  {contextOption.label}
                </DropdownMenuItem>
              );
            }
            return null;
          })}
          {children}
        </DropdownMenuContent>
      </DropdownMenu>
    </Stack>
  );
};
